import axios from "axios"
import { API_URL } from "../utils/ApiLinks"
import type { RequestDto, UpdateRequestDto, UpdateStatusDto, RequestCountersDto } from "../types/request"

// Helper for getting auth headers
const getAuthHeaders = (contentType = "application/json") => ({
  "Content-Type": contentType,
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
})

// Modify the createRentalRequest function to create a conversation after successful request submission
export const createRentalRequest = async (formData: FormData) => {
  try {

    const response = await axios.post(`${API_URL}/Requests/create-rental-request`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
    })

    if (response.data && response.data.success) {
      try {
        const propertyId = formData.get("PropertyId")
        const userProfileId = formData.get("UserProfileId")
        const message = formData.get("Message")

        const conversationDto = {
          propertyId,
          conversationType: "Solicitud de Alquiler",
          participants: [userProfileId],
          messageContent: message,
          messageType: "Texto",
        }

        await axios.post(`${API_URL}/Messages/create-conversation`, conversationDto, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        })

      } catch (error) {
        console.error("Error creating conversation:", error)
      }
    }

    return response.data
  } catch (error: any) {
    console.error("Error in createRentalRequest:", error)
    handleAuthError(error)
    return undefined
  }
}


// Modify the createPurchaseRequest function to create a conversation after successful request submission
export const createPurchaseRequest = async (formData: FormData) => {
  try {

    const response = await axios.post(`${API_URL}/Requests/create-purchase-request`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
    })

    if (response.data && response.data.success) {
      try {
        const propertyId = formData.get("PropertyId")
        const userProfileId = formData.get("UserProfileId")
        const message = formData.get("Message")

        const conversationDto = {
          propertyId,
          conversationType: "Solicitud de Compra",
          participants: [userProfileId],
          messageContent: message,
          messageType: "Texto",
        }

        await axios.post(`${API_URL}/Messages/create-conversation`, conversationDto, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        })
      } catch (error) {
        console.error("Error creating conversation:", error)
      }
    }

    return response.data
  } catch (error: any) {
    console.error("Error in createPurchaseRequest:", error)
    handleAuthError(error)
    return undefined
  }
}

// Get all requests (admin)
export const getAllRequests = async (): Promise<RequestDto[]> => {
  try {
    const response = await axios.get(`${API_URL}/Requests/get-all-requests`, {
      headers: getAuthHeaders(),
    })
    return response.data
  } catch (error: any) {
    handleAuthError(error)
    return []
  }
}

// Get requests for current user
export const getUserRequests = async (): Promise<RequestDto[]> => {
  try {
    const response = await axios.get(`${API_URL}/Requests/get-user-requests`, {
      headers: getAuthHeaders(),
    })
    return response.data
  } catch (error: any) {
    handleAuthError(error)
    return []
  }
}

// Get requests by broker
export const getRequestsByBroker = async (brokerId: number): Promise<RequestDto[]> => {
  try {
    const response = await axios.get(`${API_URL}/Requests/get-requests-by-broker/${brokerId}`, {
      headers: getAuthHeaders(),
    })
    return response.data
  } catch (error: any) {
    handleAuthError(error)
    return []
  }
}

// Get requests by client
export const getRequestsByClient = async (clientId: number): Promise<RequestDto[]> => {
  try {
    const response = await axios.get(`${API_URL}/Requests/get-requests-by-client/${clientId}`, {
      headers: getAuthHeaders(),
    })
    return response.data
  } catch (error: any) {
    handleAuthError(error)
    return []
  }
}

// Get request by ID
export const getRequestById = async (requestId: number): Promise<RequestDto | null> => {
  try {
    const response = await axios.get(`${API_URL}/Requests/get-request/${requestId}`, {
      headers: getAuthHeaders(),
    })
    return response.data
  } catch (error: any) {
    handleAuthError(error)
    return null
  }
}

// Get request counters
export const getRequestCounters = async (userProfileId: number, roleType: string): Promise<RequestCountersDto> => {
  try {
    const response = await axios.get(`${API_URL}/Requests/get-request-counters`, {
      params: { userProfileId, roleType },
      headers: getAuthHeaders(),
    })
    return response.data
  } catch (error: any) {
    handleAuthError(error)
    return { totalRequests: 0, inProgress: 0, rejected: 0, approved: 0 }
  }
}

// Update request status
export const updateRequestStatus = async (
  updateData: UpdateStatusDto,
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axios.put(`${API_URL}/Requests/update-status`, updateData, {
      headers: getAuthHeaders(),
    })
    return response.data
  } catch (error: any) {
    handleAuthError(error)
    return { success: false, message: error.message || "Error updating request status" }
  }
}

// Update request details
export const updateRequest = async (
  requestId: number,
  updateData: UpdateRequestDto,
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axios.put(`${API_URL}/Requests/update-request/${requestId}`, updateData, {
      headers: getAuthHeaders(),
    })
    return response.data
  } catch (error: any) {
    handleAuthError(error)
    return { success: false, message: error.message || "Error updating request" }
  }
}

// Delete request
export const deleteRequest = async (requestId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axios.delete(`${API_URL}/Requests/delete-request/${requestId}`, {
      headers: getAuthHeaders(),
    })
    return response.data
  } catch (error: any) {
    handleAuthError(error)
    return { success: false, message: error.message || "Error deleting request" }
  }
}

// Error handling function
const handleAuthError = (error: any) => {
  console.error("Request service error:", error)
  if (error.response) {
    throw new Error(error.response.data.message || error.message)
  } else {
    throw new Error(error.message || "An unknown error occurred")
  }
}
